import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const token = new URLSearchParams(window.location.search).get("token");

  const handleReset = async () => {
    if (!token) {
      toast.error(t("resetPassword.invalidLinkError"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("resetPassword.passwordTooShortError"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("resetPassword.passwordMismatchError"));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      toast.success(res.data?.message || t("resetPassword.successFallbackMessage"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("resetPassword.genericError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "var(--bg)" }}>
      <div className="vs-card p-8 max-w-md w-full">
        {done ? (
          <div className="text-center">
            <div className="text-5xl mb-4">✅</div>
            <h1 className="heading text-xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
              {t("resetPassword.doneTitle")}
            </h1>
            <p className="mb-6" style={{ color: "var(--text-secondary)" }}>
              {t("resetPassword.doneText")}
            </p>
            <button
              onClick={() => navigate("/")}
              className="vs-btn-primary w-full"
              style={{ background: "var(--primary)", border: "2px solid var(--primary)" }}
            >
              {t("resetPassword.goToLoginButton")}
            </button>
          </div>
        ) : (
          <>
            <h1 className="heading text-xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
              {t("resetPassword.formTitle")}
            </h1>
            <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              {t("resetPassword.formSubtitle")}
            </p>

            <div className="mb-3">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ border: "1px solid var(--border, #ddd)", background: "var(--bg)" }}>
                <Lock size={18} style={{ color: "var(--text-muted)" }} />
                <input
                  type="password"
                  placeholder={t("resetPassword.newPasswordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: "var(--text-primary)" }}
                />
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ border: "1px solid var(--border, #ddd)", background: "var(--bg)" }}>
                <Lock size={18} style={{ color: "var(--text-muted)" }} />
                <input
                  type="password"
                  placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleReset(); }}
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: "var(--text-primary)" }}
                />
              </div>
            </div>

            <button
              onClick={handleReset}
              disabled={loading}
              className="vs-btn-primary w-full"
              style={{ background: "var(--primary)", border: "2px solid var(--primary)", opacity: loading ? 0.6 : 1 }}
            >
              {loading ? t("resetPassword.submitLoading") : t("resetPassword.submitButton")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
